import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import centerLogoImg from "./img/logo.png";
import bgImg from "./img/index-bg.jpg";
import bgImgMobile from "./img/index-bg-mobile.png";

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

/** Mirrors the other pages' mobile-portrait breakpoint, used to pick index-bg-mobile.png. */
function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

/** Mirrors old/index.php's right-click/devtools-shortcut block. */
function useDisableInspect() {
  useEffect(() => {
    const blockContextMenu = (event) => event.preventDefault();
    const blockShortcutKeys = (e) => {
      if (e.keyCode === 123) return false; // F12
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) return false; // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) return false; // Ctrl+Shift+J
      if (e.ctrlKey && e.keyCode === 85) return false; // Ctrl+U
      return true;
    };
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("keydown", blockShortcutKeys);
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockShortcutKeys);
    };
  }, []);
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);
  const isMobilePortrait = useMobilePortrait();

  useDisableInspect();

  // old/index.php's BEGIN PLAY led into old/rules1.php before the room;
  // /rules is that step here and hands off to /game once read.
  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;
      setBeginLoading(true);
      try {
        navigate("/rules", { replace: true });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, navigate]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  // old/index.php's .bg background, painted on <body> instead of a page div
  // so it fills the viewport the same way the other pages' body backgrounds do.
  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    body.classList.remove("common-bg");
    body.style.backgroundImage = `url("${isMobilePortrait ? bgImgMobile : bgImg}")`;
    body.style.backgroundSize = "cover";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, [isMobilePortrait]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // show "already played" when login payload has gameover > 0 (DB stages.gameover)
  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user) return;
    if (Number(user.gameover ?? 0) > 0) {
      const redirect = user.backButtonRedirect || "/";
      Swal.fire("You have already played!", "  ", "error").then(() => {
        window.location.href = redirect;
      });
    }
  }, [status, user, error, isAdmin]);

  return (
    <div className="login-page">
      <div className="login-container">
        <img
          src={centerLogoImg}
          className="login-center-logo"
          alt="Escape To Inner Peace"
        />

        {!isAdmin ? (
          <div className="login-begin-wrap">
            <button
              type="button"
              className="begin-play-btn"
              disabled={beginLoading || status !== "succeeded" || !user}
              onClick={handleBeginPlay}
            >
              {beginLoading ? "LOADING…" : "BEGIN PLAY"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default Login;
